import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Plus } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import ViewHeader from '../../components/ViewHeader';
import AppRoutes from '../../constants/routes';
import ProductionPlantTable from '../../components/ProductionPlantTable';
import useProductionPlants from '../../hooks/useProductionPlants';
import NetworkContainer from '../../components/NetworkContainer';
import { HttpRequestStatus } from '../../components/types/types';
import useProductionPlantFieldOptions from '../../hooks/useProductionPlantFieldOptions';
import useFileDownload from '../../hooks/useFileDownload';
import SearchInput from '../../components/SearchInput';

export default function ProductionPlants() {
  const { t } = useTranslation();

  const [keyword, setKeyword] = useState('');

  const navigate = useNavigate();

  const { data, status, isSuccess } = useProductionPlants();

  const filteredProductionPlants = useMemo(() => {
    if (!isSuccess) {
      return [];
    }

    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) {
      return data;
    }

    return data.filter((plant) => plant.name.toLowerCase().includes(trimmed)
      || (plant.id || '').toString().includes(trimmed));
  }, [data, keyword, isSuccess]);

  const handleDownloadResponse = (file: File) => {
    const blob = new Blob([file]);
    const tempLink = document.createElement('a');
    tempLink.href = window.URL.createObjectURL(blob);
    tempLink.download = `production_plants_spa_${new Date().toISOString().slice(0, 10)}.xlsx`;
    tempLink.click();
  };

  const { onDownload, downloadStatus } = useFileDownload({
    callback: handleDownloadResponse,
    url: '/production-plants-export',
  });

  const {
    data: optionsData,
    status: optionsStatus,
    isSuccess: isOptionsSuccess,
  } = useProductionPlantFieldOptions();

  const statusList = [status, optionsStatus];

  return (
    <NetworkContainer status={statusList as HttpRequestStatus[]}>
      <ViewHeader
        title={t('production-plants.title')}
        description={t('production-plants.label-description')}
      >
        <Button
          icon={<Plus />}
          size="small"
          onClick={() => navigate(AppRoutes.PRODUCTION_PLANTS_NEW)}
        >
          {t('production-plants.cmd-add-supply-chain')}
        </Button>
        <Button
          label={t('common.cmd-export')}
          icon="pi pi-file-export"
          size="small"
          onClick={onDownload}
          disabled={downloadStatus.isDownloading}
        />
      </ViewHeader>
      { (isSuccess && isOptionsSuccess) && (
        <>
          <div className="field flex gap-3">
            <SearchInput
              placeholder={t('production-plants.label-search-production-plants')}
              onChange={(e) => setKeyword(e.target.value)}
              value={keyword}
            />
            <Button
              onClick={() => setKeyword('')}
              className="w-4rem"
              severity="secondary"
              icon="pi pi-times"
            />
          </div>
          <ProductionPlantTable items={filteredProductionPlants} data={optionsData} />
        </>
      )}
    </NetworkContainer>
  );
}
